import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AffiliateLayout from '../../components/AffiliateLayout/AffiliateLayout';
import api from '../../services/api';
import {
  PageTitle, PageSub, Head, StatGrid, Tile, TileNum, TileLabel, Card, TableWrap,
  Table, Spinner, Empty, NameCell, money, colors,
} from '../../components/AffiliateUI/AffiliateUI';

// Program-wide affiliate analytics, ported from the standalone
// affiliates.zona.miami portal.

const AffiliateAnalytics = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/affiliate-admin/analytics')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load analytics'));
  }, []);

  if (!data) {
    return (
      <AffiliateLayout><Spinner /></AffiliateLayout>
    );
  }

  const convRate = data.totals.clicks
    ? ((data.totals.conversions / data.totals.clicks) * 100).toFixed(1)
    : '0.0';
  const maxClicks = Math.max(1, ...data.series.map((s) => s.clicks));

  return (
    <AffiliateLayout>
          <Head>
            <div>
              <PageTitle>Analytics</PageTitle>
              <PageSub>Program-wide activity across every affiliate.</PageSub>
            </div>
          </Head>

          <StatGrid>
            <Tile><TileNum>{data.totals.clicks.toLocaleString()}</TileNum><TileLabel>Clicks</TileLabel></Tile>
            <Tile><TileNum>{data.totals.conversions.toLocaleString()}</TileNum><TileLabel>Sales</TileLabel></Tile>
            <Tile><TileNum>{convRate}%</TileNum><TileLabel>Conversion</TileLabel></Tile>
            <Tile><TileNum>{money(data.totals.gmv_cents)}</TileNum><TileLabel>GMV referred</TileLabel></Tile>
          </StatGrid>
          <StatGrid>
            <Tile><TileNum>{data.affiliates.approved}</TileNum><TileLabel>Active affiliates</TileLabel></Tile>
            <Tile><TileNum>{money(data.totals.pending_cents)}</TileNum><TileLabel>Pending commission</TileLabel></Tile>
            <Tile><TileNum>{money(data.totals.available_earned_cents)}</TileNum><TileLabel>Earned (available)</TileLabel></Tile>
            <Tile><TileNum>{money(data.totals.paid_out_cents)}</TileNum><TileLabel>Paid out</TileLabel></Tile>
          </StatGrid>

          <SectionLabel>Last 30 days</SectionLabel>
          <ChartCard>
            {data.series.length === 0 ? <Empty>No activity in the last 30 days.</Empty> : (
              <>
                <Bars>
                  {data.series.map((s) => (
                    <Bar key={s.date} title={`${s.date} · ${s.clicks} clicks · ${s.conversions} sales`}>
                      <Fill style={{ height: `${(s.clicks / maxClicks) * 100}%` }} />
                      {s.conversions > 0 && (
                        <Conv style={{ height: `${(s.conversions / maxClicks) * 100}%` }} />
                      )}
                    </Bar>
                  ))}
                </Bars>
                <Legend>
                  <span><Dot style={{ background: colors.brand }} /> Clicks</span>
                  <span><Dot style={{ background: colors.success }} /> Sales</span>
                </Legend>
              </>
            )}
          </ChartCard>

          <SectionLabel>Top affiliates</SectionLabel>
          <Card>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>#</th><th>Affiliate</th>
                    <th className="num">Sales</th><th className="num">GMV</th><th className="num">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leaderboard.map((l, i) => (
                    <tr key={l._id}>
                      <td className="strong">{i + 1}</td>
                      <td>
                        <NameCell>
                          <span className="n">{l.name || 'Unnamed'}</span>
                          {l.promo_code && <span className="code">&nbsp;{l.promo_code}</span>}
                        </NameCell>
                      </td>
                      <td className="num strong">{l.conversions}</td>
                      <td className="num">{money(l.gmv_cents)}</td>
                      <td className="num">{money(l.earned_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {data.leaderboard.length === 0 && <Empty>No sales yet.</Empty>}
          </Card>
    </AffiliateLayout>
  );
};

export default AffiliateAnalytics;

const SectionLabel = styled.div`
  color: ${colors.textMuted}; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.8px; margin: 22px 0 10px;
`;
const ChartCard = styled(Card)`padding: 18px 16px 14px;`;
const Bars = styled.div`display: flex; align-items: flex-end; gap: 3px; height: 140px;`;
const Bar = styled.div`
  flex: 1; height: 100%; position: relative;
  display: flex; align-items: flex-end; justify-content: center;
`;
const Fill = styled.div`
  width: 100%; border-radius: 3px 3px 0 0;
  background: linear-gradient(180deg, ${colors.brand} 0%, rgba(114,41,245,0.35) 100%);
  min-height: 2px;
`;
const Conv = styled.div`
  position: absolute; bottom: 0; width: 100%; border-radius: 3px 3px 0 0;
  background: ${colors.success}; opacity: 0.9; min-height: 2px;
`;
const Legend = styled.div`
  display: flex; gap: 18px; margin-top: 12px;
  span { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: ${colors.textMuted}; }
`;
const Dot = styled.i`width: 9px; height: 9px; border-radius: 3px; display: inline-block;`;
